/**
 * SHA-256 hashing for Google Ads Enhanced Conversions for Leads.
 * Hashes user-provided data (email / phone / name) before it touches the
 * dataLayer so personal data is never sent unhashed to Google.
 *
 * Normalization follows Google's spec:
 *   - email: lowercase, trim, then SHA-256
 *   - phone: E.164 ("+49…"), strip every non-digit except leading "+", then SHA-256
 *   - name : lowercase, trim, collapse whitespace, then SHA-256
 *
 * Crypto.subtle is browser-only — these helpers only work in client components.
 */

async function sha256(value: string): Promise<string> {
  const buf = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** DE-default E.164 normalization. Leading "0" becomes "+49…". */
function normalizePhone(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("00")) return "+" + digits.slice(2);
  if (digits.startsWith("0")) return "+49" + digits.slice(1);
  return "+49" + digits; // fallback: assume DE
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export type RawUserData = {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
};

export type HashedUserData = {
  sha256_email_address?: string;
  sha256_phone_number?: string;
  address?: {
    sha256_first_name?: string;
    sha256_last_name?: string;
  };
};

/** Hash whichever fields are present; skip missing ones. */
export async function hashUserData(raw: RawUserData): Promise<HashedUserData> {
  const out: HashedUserData = {};
  if (raw.email) out.sha256_email_address = await sha256(normalizeEmail(raw.email));
  if (raw.phone) out.sha256_phone_number = await sha256(normalizePhone(raw.phone));
  if (raw.firstName || raw.lastName) {
    out.address = {};
    if (raw.firstName) out.address.sha256_first_name = await sha256(normalizeName(raw.firstName));
    if (raw.lastName) out.address.sha256_last_name = await sha256(normalizeName(raw.lastName));
  }
  return out;
}

/** Split a single "Vorname Nachname" string into first/last. Returns undefined
 *  for either part if not derivable (e.g. only one name token). */
export function splitName(fullName: string): { firstName?: string; lastName?: string } {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}
